import { remove } from 'lodash';
import path from 'path';
import { v4 as uuidv4 } from 'uuid';

import { awaitTo, isExternal } from '@/utils/utils';
import {
  getUserInfo as getUI,
  getAuthMenu as getAM,
  getOffice as getOF,
  getTask as getTK,
  profile,
  logout,
  syncLanguage
} from '@/api/auth';
import {
  getAlarmType as getAT,
  getMaterialType as getMT,
  getStatType as getST,
  getDeviceModels as getMD
} from '@/api/baseInfo';
import Type from '@/store/mutation-type';
import { contanstRoutes, asyncRoutes } from '@/router';
import fixedMenus from '@/fixedMenu';
import EnvUtils from '@/utils/EnvUtils';
import { isEmpty } from '@/utils/is';

const devAuth = {};

function hasPermission(routes, codes) {
  if (routes.meta && routes.meta.code) {
    return !!codes[routes.meta.code];
  } else {
    return true;
  }
}
function filterAsyncRoutes(routes, codes) {
  let res = [];
  routes.forEach(r => {
    const tmp = { ...r };
    if (hasPermission(tmp, codes)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children, codes);
      }
      res.push(tmp);
    }
  });
  return res;
}

function formatPath(basePath, routePath) {
  if (isExternal(routePath)) {
    return routePath;
  }
  if (isExternal(basePath)) {
    return basePath;
  }
  const rP = routePath.includes(':') ? routePath.substr(0, routePath.indexOf(':') - 1) : routePath;
  return path.resolve(basePath, rP);
}

function getHideProp(meta) {
  if (!meta.hasOwnProperty('hidden')) return false;
  return !!meta.hidden;
}

function filterLayoutMenu(routes, basePath = '/') {
  let menus = [];
  routes.forEach(r => {
    const tmp = { ...r };
    const meta = tmp.meta;
    if (tmp.children) {
      if (meta && meta.title) {
        menus.push({
          title: meta.title,
          icon: meta.icon || 'el-icon-document',
          code: meta.code,
          order: meta.order,
          hidden: getHideProp(meta),
          id: uuidv4(),
          route: { path: formatPath(basePath, tmp.path) },
          children: filterLayoutMenu(tmp.children, tmp.path)
        });
      } else {
        menus = menus.concat(filterLayoutMenu(tmp.children, tmp.path));
      }
    } else {
      if (meta && meta.title) {
        menus.push({
          title: meta.title,
          icon: meta.icon || 'el-icon-document',
          code: meta.code,
          order: meta.order,
          hidden: getHideProp(meta),
          id: uuidv4(),
          route: { path: formatPath(basePath, tmp.path) }
        });
      }
    }
  });
  return menus;
}
export default {
  namespaced: true,
  state: {
    routes: [],
    addRoutes: [],
    userInfo: '', // 登录人信息
    authMenu: {}, // 权限按钮
    tasks: [], // 任务
    offices: [], // 机构
    alarmTypes: [], // 报警类型
    materialTypes: [], // 物质类型
    busiTypes: [], // 统计类型
    deviceModels: [], // 设备类型
    profile: null, // 个性化
    validData: false
  },
  getters: {
    layoutMenu(state) {
      return filterLayoutMenu(state.routes)
        .sort((a, b) => a.order - b.order)
        .concat(fixedMenus);
    }
  },
  mutations: {
    [Type.GET_ROUTES](state, routes) {
      state.addRoutes = routes;
      state.routes = contanstRoutes.concat(routes);
    },
    [Type.USER_INFO](state, data) {
      state.userInfo = data;
    },
    [Type.GET_OFFICES](state, data) {
      const copyData = JSON.parse(JSON.stringify(data));
      function deleteSN(d) {
        remove(d, e => {
          return e.nodeType === 2;
        });
        d.forEach(e => {
          deleteSN(e.children);
        });
      }
      deleteSN(copyData);
      state.offices = copyData;
    },
    [Type.GET_AUTH_MENU](state, data) {
      state.authMenu = data;
    },
    [Type.GET_AUTH_TASK](state, data) {
      state.tasks = data;
    },
    [Type.ALARM_TYPE](state, data) {
      state.alarmTypes = data;
    },
    [Type.MATERIAL_TYPE](state, data) {
      state.materialTypes = data;
    },
    [Type.BUSI_TYPE](state, data) {
      state.busiTypes = data;
    },
    [Type.DEVICE_MODELS](state, data) {
      state.deviceModels = data;
    },
    [Type.GET_PROFILE](state, data) {
      state.profile = data;
    },
    [Type.GET_VALID_DATA](state, data) {
      state.validData = data;
    }
  },
  actions: {
    async initBasicData({ state, dispatch, rootState }) {
      const list = [];

      if (!state.profile) {
        list.push(dispatch('getProfile'));
      }

      if (!state.userInfo) {
        list.push(dispatch('getUserInfo'));
      }

      if (!rootState.socket.deviceTree) {
        list.push(dispatch('getDeviceModel'));
        list.push(dispatch('syncLanguage'));
      }

      if (list.length > 0) {
        const [err] = await awaitTo(Promise.all(list));
        if (err) return false;
      }

      return true;
    },
    async syncLanguage() {
      try {
        await syncLanguage();
      } catch (error) {
        console.log(error);
      }

      return true;
    },
    async getDeviceModel({ commit }) {
      await getOF({ busiType: 1 }).then(res => {
        commit(`socket/${Type.GET_DEVICE_TREE}`, res.data, { root: true });
      });
    },
    async updateDeviceModel({ commit }) {
      await getOF({ busiType: 1 }).then(res => {
        commit(`socket/${Type.UPDATE_DEVICE_TREE_LANG}`, res.data, { root: true });
      });
    },
    getUserInfo({ commit }) {
      getUI().then(res => {
        commit(Type.USER_INFO, res.data);
      });
    },
    getOffices({ commit }) {
      getOF({ busiType: 2 }).then(res => {
        commit(Type.GET_OFFICES, res.data);
      });
    },
    getAuthMenu({ commit }) {
      return getAM().then(res => {
        if (EnvUtils.is('development', true)) Object.assign(res.data, devAuth);
        const accessRoutes = filterAsyncRoutes(asyncRoutes, res.data);
        commit(Type.GET_AUTH_MENU, res.data);
        commit(Type.GET_ROUTES, accessRoutes);
        return accessRoutes;
      });
    },
    getTask({ commit }) {
      getTK().then(res => {
        commit(Type.GET_AUTH_TASK, res.data);
      });
    },
    getAlarmType({ commit }) {
      getAT().then(res => {
        commit(Type.ALARM_TYPE, res.data);
      });
    },
    getMaterialType({ commit }) {
      const mat = getMT();
      mat.then(res => {
        commit(Type.MATERIAL_TYPE, res.data);
      });

      return mat;
    },
    getStatType({ commit }, params) {
      const st = getST(params);

      st.then(res => {
        commit(Type.BUSI_TYPE, res.data);
      });

      return st;
    },
    getDeviceModels({ commit }, params) {
      getMD(params).then(res => {
        commit(Type.DEVICE_MODELS, res.data);
      });
    },
    getProfile({ commit }) {
      return profile().then(res => {
        commit(Type.GET_PROFILE, res.data);
        // 模拟本地端
        // commit(Type.GET_PROFILE, Object.assign(res.data, { mapTile: '/map' }));
        commit(Type.GET_VALID_DATA, true);

        let flag = res.data?.enableAlarmDisposition;
        flag = !(isEmpty(flag) || [0, '0'].includes(flag));

        if (!flag) {
          commit(`mqtt/${Type.CLEAR_ALARM_DATA}`, null, { root: true });
        }

        commit(
          `device/${Type.SET_MONITOR_CONFIG}`,
          {
            enableAlarmDisposition: flag
          },
          { root: true }
        );
      });
    },
    async logout() {
      const [err] = await awaitTo(logout());

      if (err) console.error('登出失败');

      return true;
    },
    clearAll({ commit }) {
      commit(Type.GET_AUTH_MENU, {});
      commit(Type.USER_INFO, '');
      commit(Type.GET_PROFILE, null);
      commit(Type.GET_VALID_DATA, false);
    }
  }
};
